import IntranetLayout from "@/components/IntranetLayout";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Scale, Plus, Calendar, CheckCircle2, Clock, AlertTriangle } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";

const CATEGORIES = ["Flag State (RMI)", "Class Society (BV/ABS)", "ISM", "ISPS", "MLC", "Audit", "Permit", "Other"];

const EmployeeCompliance = () => {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ title: "", category: "Other", due_date: "", status: "pending", assigned_to: "", description: "" });

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["compliance-items"],
    queryFn: async () => {
      const { data, error } = await supabase.from("compliance_items").select("*").order("due_date", { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  const addMutation = useMutation({
    mutationFn: async (values: typeof form) => {
      const { error } = await supabase.from("compliance_items").insert({ ...values, due_date: values.due_date || null });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["compliance-items"] });
      toast.success("Item added");
      setForm({ title: "", category: "Other", due_date: "", status: "pending", assigned_to: "", description: "" });
      setDialogOpen(false);
    },
    onError: (e) => toast.error(e.message),
  });

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase.from("compliance_items").update({ status }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["compliance-items"] });
      toast.success("Status updated");
    },
  });

  const overdue = items.filter((i: any) => i.status !== "completed" && i.due_date && new Date(i.due_date) < new Date());
  const upcoming = items.filter((i: any) => {
    if (i.status === "completed" || !i.due_date) return false;
    const diff = new Date(i.due_date).getTime() - Date.now();
    return diff > 0 && diff < 30 * 86400000;
  });

  const statusColor = (status: string, dueDate: string | null) => {
    if (status === "completed") return "default";
    if (dueDate && new Date(dueDate) < new Date()) return "destructive";
    return "secondary";
  };

  return (
    <IntranetLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl text-foreground">Compliance & Regulatory</h1>
          <p className="text-sm text-muted-foreground font-body mt-1">Flag state requirements, class society audits, ISM/ISPS/MLC documentation</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => toast.success("Created: Add Item", { description: "New entry added." })} size="sm" className="bg-teal hover:bg-teal-light text-white gap-2"><Plus className="w-4 h-4" /> Add Item</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Compliance Item</DialogTitle></DialogHeader>
            <form onSubmit={(e) => { e.preventDefault(); addMutation.mutate(form); }} className="space-y-4">
              <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-muted-foreground font-body block mb-1">Due Date</label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
                <Input placeholder="Assigned To" value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} />
              </div>
              <Textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} />
              <Button type="submit" className="w-full bg-teal hover:bg-teal-light text-white" disabled={addMutation.isPending}>Add Item</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="w-9 h-9 rounded-lg bg-teal/10 flex items-center justify-center"><Scale className="w-4.5 h-4.5 text-teal" /></div><div><p className="text-xl font-display text-foreground">{items.length}</p><p className="text-xs text-muted-foreground font-body">Total Items</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center"><Clock className="w-4.5 h-4.5 text-amber-500" /></div><div><p className="text-xl font-display text-foreground">{upcoming.length}</p><p className="text-xs text-muted-foreground font-body">Due This Month</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="w-9 h-9 rounded-lg bg-destructive/10 flex items-center justify-center"><AlertTriangle className="w-4.5 h-4.5 text-destructive" /></div><div><p className="text-xl font-display text-foreground">{overdue.length}</p><p className="text-xs text-muted-foreground font-body">Overdue</p></div></CardContent></Card>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-teal/30 border-t-teal rounded-full animate-spin" /></div>
      ) : items.length === 0 ? (
        <Card><CardContent className="p-12 text-center"><Scale className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" /><p className="text-muted-foreground font-body">No compliance items yet</p></CardContent></Card>
      ) : (
        <div className="space-y-3">
          {items.map((item: any) => (
            <Card key={item.id}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-sm font-medium text-foreground font-body">{item.title}</h3>
                    <Badge variant="outline" className="text-[10px]">{item.category}</Badge>
                  </div>
                  {item.description && <p className="text-xs text-muted-foreground font-body line-clamp-1">{item.description}</p>}
                  <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground font-body">
                    {item.due_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(item.due_date).toLocaleDateString()}</span>}
                    {item.assigned_to && <span>Assigned: {item.assigned_to}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={statusColor(item.status, item.due_date) as any}>
                    {item.status === "completed" ? "Completed" : item.due_date && new Date(item.due_date) < new Date() ? "Overdue" : "Pending"}
                  </Badge>
                  {item.status !== "completed" && (
                    <Button variant="ghost" size="sm" className="text-xs h-7 text-teal" onClick={() => updateStatus.mutate({ id: item.id, status: "completed" })}>
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Complete
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </IntranetLayout>
  );
};

export default EmployeeCompliance;
